/**
 * PDP / featured product: stick the shorter column (gallery vs details).
 * Only toggles when the shorter column actually changes — avoids flicker with <safe-sticky>.
 */
(() => {
  const MQ = '(min-width: 1000px)';
  const HYSTERESIS = 8;
  const observed = new WeakSet();
  const rafByProduct = new WeakMap();

  const applySticky = (product, next) => {
    const gallery = product.querySelector('.product-gallery');
    const info = product.querySelector('.product-info');

    if (!gallery || !info) return;

    const prev = product.dataset.stickyColumn || '';

    if (prev === next) return;

    product.dataset.stickyColumn = next;

    gallery.classList.toggle('is-sticky-column', next === 'gallery');

    const infoShouldStick = next === 'info';
    const infoWasSticky = info.classList.contains('is-sticky-column');

    info.classList.toggle('is-sticky-column', infoShouldStick);

    if (infoWasSticky && !infoShouldStick) {
      info.style.removeProperty('top');
    }
  };

  const update = (product) => {
    const gallery = product.querySelector('.product-gallery');
    const info = product.querySelector('.product-info');

    if (!gallery || !info) return;

    if (!window.matchMedia(MQ).matches) {
      applySticky(product, '');
      return;
    }

    // Measure without removing sticky — position:sticky does not change offsetHeight
    const galleryHeight = gallery.offsetHeight;
    const infoHeight = info.offsetHeight;
    const diff = infoHeight - galleryHeight;
    const prev = product.dataset.stickyColumn || '';

    let next = prev;

    if (diff > HYSTERESIS) {
      next = 'gallery';
    } else if (diff < -HYSTERESIS) {
      next = 'info';
    } else if (!prev) {
      if (diff > 0) next = 'gallery';
      else if (diff < 0) next = 'info';
    }

    applySticky(product, next);
  };

  const schedule = (product) => {
    if (rafByProduct.get(product)) return;

    rafByProduct.set(
      product,
      requestAnimationFrame(() => {
        rafByProduct.delete(product);
        update(product);
      })
    );
  };

  const observe = (product) => {
    if (observed.has(product)) return;

    observed.add(product);

    const resizeObserver = new ResizeObserver(() => schedule(product));
    const gallery = product.querySelector('.product-gallery');
    const info = product.querySelector('.product-info');

    if (gallery) resizeObserver.observe(gallery);
    if (info) resizeObserver.observe(info);

    window.matchMedia(MQ).addEventListener('change', () => schedule(product));
    schedule(product);
  };

  const init = (root = document) => {
    root.querySelectorAll?.('.product')?.forEach(observe);
    if (root instanceof Element && root.matches?.('.product')) {
      observe(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        init(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
